import { Menu } from "lucide-react";
import { useLocation } from "react-router-dom";

import { BrandMark } from "@/components/layout/BrandMark";
import { AccountMenu } from "@/components/layout/AccountMenu";
import { Badge } from "@/components/ui/badge";
import { ROLE_PILL_TEXT, getSectionLabel } from "@/components/layout/nav-config";
import { useAuth } from "@/contexts/auth-context";
import { useSubscriptionState } from "@/hooks/queries/useSubscriptionState";
import { subscriptionPill } from "@/lib/subscription-copy";

interface TopBarProps {
  /** Opens the mobile nav drawer; the trigger only shows ≤1120px. */
  onOpenNav: () => void;
}

// Role pill — always carries explicit text (never colour-only), satisfying the
// "more than colour" a11y rule. gh-625: admin roles only — a clinician's role
// slot renders nothing (mockups 02/11/12 show only the subscription pill, and
// the old static "Authorized for live scoring" copy contradicted every
// non-authorized state). The state-aware SubscriptionPill is the clinician's
// single access-state statement.
function RolePill({ role }: { role: string | undefined }) {
  const text = (role && ROLE_PILL_TEXT[role]) ?? "Signed in";
  return (
    <span className="hidden items-center rounded-[var(--radius-full)] border border-[color:var(--border-brand-alt)] bg-[color:var(--bg-brand-primary)] px-3 py-1 text-xs font-semibold text-[color:var(--text-brand-secondary)] sm:inline-flex">
      {text}
    </span>
  );
}

// Story 16.2 / Franklin#445 — the gated legacy clinician's header pill (a
// gated clinician gets neither the subscription pill nor — gh-625 — any role
// pill). Stage-aware: once the cert is approved
// (cert_verified) the account is still locked but "pending verification" is no
// longer true, so the pill reads "awaiting activation". Explicit text (never
// colour-only) for a11y.
function PendingPill({ certVerified }: { certVerified: boolean }) {
  return (
    <Badge variant="warning" className="hidden sm:inline-flex">
      {certVerified
        ? "Account locked · awaiting activation"
        : "Account disabled · pending verification"}
    </Badge>
  );
}

// Story 6.9 — subscription state pill (Screens 02/12 topbar). Shown for
// clinicians only (admins have no subscription), and never alongside the legacy
// "pending" pill — see the render gate below, which is why no legacy-onboarding
// context is passed to the mapper here.
//
// The chip no longer owns a discriminator or a copy table. Both come from
// `subscriptionPill` (`lib/subscription-copy.ts`), the single mapper the whole
// product reads — the ledger's "billing display-state consolidation" entry, and
// the end of the drift that produced DW-437. `null` is the unresolved state
// (`loading`, errored, malformed payload): render nothing rather than flash a
// guess. Explicit text always accompanies the variant — never colour alone
// (WCAG "more than colour"); the mapper guarantees it.
//
// Two chip strings changed when the mapper landed, and neither was a slip:
// "Pending calibration" → "Activating after calibration" (16.6's UX §13.11 name,
// which the PM ruling of 2026-08-31 left standing) and, for an org seat past its
// lapse instant, "Subscription lapsed" → "Lapsed — read-only" (Story 13.6 /
// UX §13.11 — that lapse is a hard, settled block, and one mapper cannot hold
// two strings for one state).
function SubscriptionPill() {
  const subscription = useSubscriptionState();
  const pill = subscriptionPill({ subscription });
  if (!pill) return null;
  return (
    <Badge variant={pill.variant} className="hidden sm:inline-flex">
      {pill.text}
    </Badge>
  );
}

export function TopBar({ onOpenNav }: TopBarProps) {
  const { user, isLegacyGated } = useAuth();
  const { pathname } = useLocation();
  const section = getSectionLabel(pathname, user?.role);

  return (
    <header className="flex items-center justify-between gap-4 border-b border-[color:var(--border-primary)] pb-4">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={onOpenNav}
          aria-label="Open navigation"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-md)] border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--bg-tertiary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-primary)] min-[1121px]:hidden"
        >
          <Menu aria-hidden="true" className="h-5 w-5" />
        </button>
        <BrandMark subtitle={section} />
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {/* Subscription pill — clinician-only and never alongside the legacy "pending"
            pill (a gated legacy clinician has no live subscription yet). */}
        {user?.role === "clinician" && !isLegacyGated ? <SubscriptionPill /> : null}
        {/* gh-625 — clinicians get NO role pill: the SubscriptionPill above is
            their single access-state statement (screens 02/11/12). */}
        {isLegacyGated ? (
          <PendingPill certVerified={Boolean(user?.certVerified)} />
        ) : user?.role === "clinician" ? null : (
          <RolePill role={user?.role} />
        )}
        <AccountMenu />
      </div>
    </header>
  );
}
